import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import moment from 'moment/moment'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faCreditCard,
  faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  marginBottom: 10,
  backgroundColor: 'rgb(227, 230, 255, 0.5)',
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  whiteSpace: 'nowrap',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  verticalAlign: 'top',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ButtonIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${lightTheme.palette.primary.b2}`,
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '0 4px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    border: `2px solid ${lightTheme.palette.primary.b2}`,
    backgroundColor: 'rgba(102, 126, 234, 0.06)',
  },
}))

const StyledActionMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 16,
    minWidth: 220,
    border: '1px solid rgba(99, 102, 241, 0.15)',
    boxShadow: '0 24px 60px rgba(99, 102, 241, 0.18)',
  },
}))

const StyledActionMenuItem = styled(MenuItem)(({ theme }) => ({
  px: 2.5,
  py: 1.25,
  borderRadius: 12,
  gap: 10,
  color: lightTheme.palette.text.primary,
  fontFamily: 'Century Gothic',
  fontSize: 14,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '& .menuActionIcon': {
    minWidth: 24,
    color: '#667eea',
  },
}))

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'statusvariant',
})(({ statusvariant }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  height: 24,
  borderRadius: '8px',
  ...(statusvariant === 'active' && {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    color: '#2e7d32',
  }),
  ...(statusvariant === 'pay' && {
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea',
  }),
}))

const formatAmount = (amount) => {
  try {
    const currency = sessionStorage.getItem('currency')
      ? JSON.parse(sessionStorage.getItem('currency'))
      : { currentRate: 1, devise: 'CHF' }
    return `${currency.devise || 'CHF'} ${(
      (amount || 0) * (currency.currentRate || 1)
    ).toFixed(2)} `
  } catch {
    return `CHF ${(amount || 0).toFixed(2)}`
  }
}

const AtelierTable = ({ ateliers, handlePayementAtelier }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [menuAtelier, setMenuAtelier] = React.useState(null)

  const handleMenuOpen = (event, atelier) => {
    setAnchorEl(event.currentTarget)
    setMenuAtelier(atelier)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuAtelier(null)
  }

  const handlePay = (atelier) => {
    handlePayementAtelier(atelier)
    handleMenuClose()
  }

  const isPaid = (atelier) => atelier.status === 'payer'

  const renderStatus = (atelier, size = 'small') => {
    if (isPaid(atelier)) {
      return (
        <StatusChip
          size={size}
          statusvariant="active"
          label={t('status.active')}
          icon={<FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />}
        />
      )
    }
    return (
      <Tooltip title={t('button.pay')} arrow>
        <Box
          component="span"
          sx={{ cursor: 'pointer', display: 'inline-flex' }}
          onClick={() => handlePay(atelier)}
        >
          <StatusChip
            size={size}
            statusvariant="pay"
            label={t('button.pay')}
            icon={
              <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 12 }} />
            }
          />
        </Box>
      </Tooltip>
    )
  }

  return (
    <TablePanelBox value={0} index={0}>
      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table
          stickyHeader
          aria-label="ateliers table"
          sx={{ minWidth: { xs: 260, sm: 'auto' } }}
        >
          <HeaderTab>
            <TableRow>
              <TitleHead sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('profile.date')}
              </TitleHead>
              <TitleHead sx={{ minWidth: { xs: 150, sm: 'auto' } }}>
                {t('header.atelier')}
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('profile.price')}
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('profile.firstPrice')}
              </TitleHead>
              <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('profile.status')}
              </TitleHead>
              <TitleHead
                sx={{
                  width: { xs: 56, sm: 90 },
                  textAlign: 'center',
                }}
              >
                {t('profile.actions')}
              </TitleHead>
            </TableRow>
          </HeaderTab>
          <BodyTable>
            {ateliers && ateliers.length > 0 ? (
              ateliers.map((atelier, index) => (
                <TableRow
                  key={
                    atelier.id ??
                    `${atelier.date}-${atelier.atelier?.id ?? index}`
                  }
                >
                  <StyledTableCell
                    sx={{ display: { xs: 'none', md: 'table-cell' } }}
                  >
                    {moment(atelier.date)
                      .tz('Europe/Paris')
                      .format('DD/MM/YYYY HH:mm')}
                  </StyledTableCell>
                  <StyledTableCell sx={{ minWidth: { xs: 150, sm: 'auto' } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#1f2937',
                          lineHeight: 1.35,
                        }}
                      >
                        {atelier.atelier?.titre}
                      </Typography>

                      <Box
                        sx={{
                          display: { xs: 'flex', md: 'none' },
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {t('profile.date')}:
                          </Typography>
                          <Typography variant="caption">
                            {moment(atelier.date)
                              .tz('Europe/Paris')
                              .format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {t('profile.price')}:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {formatAmount(atelier.prix)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {t('profile.firstPrice')}:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: '#667eea' }}
                          >
                            {formatAmount(atelier.prixPromo)}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {renderStatus(atelier, 'small')}
                        </Box>
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatAmount(atelier.prix)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#667eea' }}
                    >
                      {formatAmount(atelier.prixPromo)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                  >
                    {renderStatus(atelier)}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {isPaid(atelier) ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          fontSize={18}
                          color={lightTheme.palette.success.main}
                          title={t('status.active')}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, atelier)}
                          aria-label={t('profile.actions')}
                        >
                          <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            color={lightTheme.palette.primary.b2}
                            fontSize={16}
                          />
                        </IconButton>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        justifyContent: 'center',
                      }}
                    >
                      {!isPaid(atelier) && (
                        <Tooltip title={t('button.pay')} arrow>
                          <ButtonIcon onClick={() => handlePay(atelier)}>
                            <FontAwesomeIcon
                              icon={faCreditCard}
                              fontSize={16}
                              color={lightTheme.palette.primary.b2}
                            />
                          </ButtonIcon>
                        </Tooltip>
                      )}
                      {isPaid(atelier) && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          fontSize={18}
                          color={lightTheme.palette.success.main}
                          title={t('status.active')}
                        />
                      )}
                    </Box>

                    <StyledActionMenu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && menuAtelier === atelier}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      PaperProps={{
                        sx: {
                          p: 1,
                          bgcolor: '#fff',
                        },
                      }}
                    >
                      {!isPaid(atelier) && (
                        <StyledActionMenuItem
                          onClick={() => handlePay(atelier)}
                        >
                          <FontAwesomeIcon
                            icon={faCreditCard}
                            className="menuActionIcon"
                            fontSize={14}
                          />
                          <Typography variant="body2">
                            {t('button.pay')}
                          </Typography>
                        </StyledActionMenuItem>
                      )}
                    </StyledActionMenu>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2">
                    {t('dashboard.noAteliers', 'Aucun événement')}
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}
          </BodyTable>
        </Table>
      </Box>
    </TablePanelBox>
  )
}

export default AtelierTable
